/**
 * Callout geometry: where a callout box may sit and how its leader reaches
 * the mark it belongs to.
 *
 * The rules, taken from the inspectors' hand markups:
 *   • the leader leaves the box at the centre of its top or bottom edge, never a side (2026-10-24,
 *     Ricky: "the line is coming from out of the side instead of the correct position"), and never
 *     doubles back past the edge it left by
 *   • the arrowhead meets its mark's outline squarely (never at less than 45° to it), across a
 *     band rather than along it, on the side facing the box
 *   • one callout may point at several marks: its arrows share that root and fan out
 *   • only the arrow's tip touches a mark — the line never crosses one, nor another
 *     callout, a note or another leader: it takes the shortest way round them all
 *     (route.ts), or comes in at the end of its own mark when that is shorter
 *   • the box never covers a mark, and never sits so close to its own mark
 *     that the arrowhead would slip under it (`minGap`)
 *
 * Everything here is pure and works in image pixels. Polygons are flat
 * [x0,y0,x1,y1,…] arrays; marks are flattened once per shape object.
 */
import type { Shape, Vec } from '../../shared/doc.ts';
import { anchorInside, shapeToPolygon } from './outline.ts';
import { dist, nearestOnPolyline, nearestOnSegment, pointInPoly, polyBounds, rectContains, rectsOverlap, segmentIntersectsRect, segmentsIntersect, type Rect } from './geom.ts';
import { blockedBy, findPath, lineObstacle, pathClear, polyObstacle, rectObstacle, type Obstacle } from './route.ts';

export interface MarkPoly {
  id: string;
  pts: number[];
  bounds: Rect;
  /** Evenly spaced points along the outline (arrow tip candidates). */
  samples: Vec[];
  /**
   * The middle of the mark: halfway between its top and bottom edges over the centre of its area.
   * An arrow aims at the centre of the top edge or of the bottom edge by default — straight above
   * or below this point — and the side a box is on is judged against it.
   */
  mid: Vec;
}

export type LeaderSide = 'top' | 'bottom';
/** An end of a mark, for an arrow that comes in along the mark instead of onto its side. */
export type MarkEnd = 'left' | 'right';

export interface LeaderRoute {
  /** Polyline from the box edge to the tip (tip last). */
  pts: Vec[];
  tip: Vec;
  /** Unit direction of the final segment. */
  dir: Vec;
  side: LeaderSide;
  /** The end of the mark the head points into, when it does not point at the mark's side. */
  face: MarkEnd | null;
  /** True when the line crosses no mark and no box, and the arrowhead has room. */
  clear: boolean;
  /** The plain route would have touched something: this one found its way round. */
  detour?: boolean;
  /** How far each corner is eased into an arc (one per point; 0 at both ends): as far as stays clear of everything. */
  radii?: number[];
  /** Which of its callout's arrows (its place in `arrowsOf`, arrows taken off counted). */
  arrow?: number;
}

/** What else is on the plan for a leader to keep clear of. */
export interface Around {
  /** The other callouts' and the notes' boxes. */
  boxes: readonly Rect[];
  /** Leaders already drawn (other callouts', and this callout's other arrows): not to be crossed if there is any way. */
  leaders: readonly (readonly Vec[])[];
}
const NOTHING: Around = { boxes: [], leaders: [] };

/** What one arrow of a callout aims at. */
export interface LeaderAim {
  /** Its mark (null = a bare point of the plan). */
  mark: MarkPoly | null;
  /** The mark's middle, or the point on its outline where the person put the tip. */
  point: Vec;
  /** Put there by hand: the tip stays, whatever else is on the plan. */
  pinned?: boolean;
  /** Put on one of the mark's ends (an old pin: a tip put by hand is now anywhere on the outline, square to it). */
  face?: MarkEnd;
  /** Which of its callout's arrows (its place in `arrowsOf`). */
  arrow?: number;
  /** Taken off its mark: not routed nor drawn (its mark still says where the box belongs). */
  off?: boolean;
}

/**
 * How long a leader's last straight run is, at the least, as a multiple of the head's length: the
 * line always runs straight into the back of its head, along the head's own axis, far enough that
 * the head reads as on the end of the line (2026-10-24, Adam: never a line coming into a head's side).
 */
export const HEAD_RUN = 1.5;

export interface CalloutSpace {
  marks: MarkPoly[];
  bounds: Rect;
  u: number;
  /** Least distance from the box edge to the arrow tip (the arrowhead plus breathing room). */
  minGap: number;
  arrowLen: number;
  /** The straight drop from the box before the leader angles off. */
  stub: number;
  /** How far short of the mark's outline the tip stops. */
  tipGap: number;
}

/* ------------------------------------------------------------- polygons */

const POLYS = new WeakMap<Shape, { pts: number[]; bounds: Rect; samples: Vec[]; mid: Vec }>();

/** A mark's outline as a polygon, cached per shape object (documents are immutable). */
export function markPoly(id: string, shape: Shape): MarkPoly {
  let c = POLYS.get(shape);
  if (!c) {
    const pts = shapeToPolygon(shape);
    const bounds = polyBounds(pts);
    const per = perimeter(pts);
    c = { pts, bounds, samples: outlineSamples(pts, Math.max(per / 72, 2)), mid: middleOf(pts, bounds) };
    POLYS.set(shape, c);
  }
  return { id, ...c };
}

/** The ys where the vertical at x crosses a closed outline, topmost first. */
function crossingsAt(pts: number[], x: number): number[] {
  const n = pts.length / 2;
  const ys: number[] = [];
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const ax = pts[2 * i]!,
      ay = pts[2 * i + 1]!,
      bx = pts[2 * j]!,
      by = pts[2 * j + 1]!;
    if (!((ax <= x && bx >= x) || (bx <= x && ax >= x)) || ax === bx) continue;
    ys.push(ay + ((x - ax) * (by - ay)) / (bx - ax));
  }
  return ys.sort((a, b) => a - b);
}

/** Over the centre of the mark's area (a curved band's centroid may lie off the band: only its x is taken), halfway down the mark there. */
function middleOf(pts: number[], bounds: Rect): Vec {
  const inset = Math.min(2, bounds.w / 4);
  const x = Math.max(bounds.x + inset, Math.min(bounds.x + bounds.w - inset, anchorInside(pts, true).x));
  const ys = crossingsAt(pts, x);
  return ys.length ? { x, y: (ys[0]! + ys[ys.length - 1]!) / 2 } : { x, y: bounds.y + bounds.h / 2 };
}

function perimeter(pts: number[]): number {
  let l = 0;
  const n = pts.length / 2;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    l += Math.hypot(pts[2 * j]! - pts[2 * i]!, pts[2 * j + 1]! - pts[2 * i + 1]!);
  }
  return l;
}

/** Points every `spacing` along a closed polygon's outline. */
export function outlineSamples(pts: number[], spacing: number): Vec[] {
  const n = pts.length / 2;
  const out: Vec[] = [];
  if (n === 0) return out;
  let carry = 0;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const ax = pts[2 * i]!,
      ay = pts[2 * i + 1]!,
      bx = pts[2 * j]!,
      by = pts[2 * j + 1]!;
    const l = Math.hypot(bx - ax, by - ay);
    let t = carry;
    while (t <= l) {
      const k = l === 0 ? 0 : t / l;
      out.push({ x: ax + (bx - ax) * k, y: ay + (by - ay) * k });
      t += spacing;
    }
    carry = t - l;
  }
  if (!out.length) out.push({ x: pts[0]!, y: pts[1]! });
  return out;
}

/** Does a rectangle touch a polygon's area or outline? */
export function rectOverlapsPoly(r: Rect, mk: { pts: number[]; bounds: Rect }): boolean {
  if (!rectsOverlap(r, mk.bounds)) return false;
  const pts = mk.pts;
  const n = pts.length / 2;
  for (let i = 0; i < n; i++) if (rectContains(r, { x: pts[2 * i]!, y: pts[2 * i + 1]! })) return true;
  const corners = [
    { x: r.x, y: r.y },
    { x: r.x + r.w, y: r.y },
    { x: r.x + r.w, y: r.y + r.h },
    { x: r.x, y: r.y + r.h },
  ];
  for (const c of corners) if (pointInPoly(c, pts)) return true;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    if (segmentIntersectsRect({ x: pts[2 * i]!, y: pts[2 * i + 1]! }, { x: pts[2 * j]!, y: pts[2 * j + 1]! }, r)) return true;
  }
  return false;
}

/** Does the segment a→b pass through a polygon (cross its outline or run inside it)? */
export function segmentCrossesPoly(a: Vec, b: Vec, mk: { pts: number[]; bounds: Rect }): boolean {
  if (!segmentIntersectsRect(a, b, mk.bounds)) return false;
  const pts = mk.pts;
  const n = pts.length / 2;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    if (segmentsIntersect(a, b, { x: pts[2 * i]!, y: pts[2 * i + 1]! }, { x: pts[2 * j]!, y: pts[2 * j + 1]! })) return true;
  }
  return pointInPoly({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, pts);
}

function inflateXY(r: Rect, dx: number, dy: number): Rect {
  return { x: r.x - dx, y: r.y - dy, w: r.w + 2 * dx, h: r.h + 2 * dy };
}

/* -------------------------------------------------------------- leaders */

export function leaderStart(rect: Rect, side: LeaderSide): Vec {
  return { x: rect.x + rect.w / 2, y: side === 'bottom' ? rect.y + rect.h : rect.y };
}
/** The way out of a box by one of its edges. */
const OUT: Record<LeaderSide, Vec> = { top: { x: 0, y: -1 }, bottom: { x: 0, y: 1 } };

/** The tip pulled back a little towards `from`, so the line itself stops short of the outline. */
function pullBack(from: Vec, tip: Vec, by: number): Vec {
  const d = dist(from, tip);
  if (d <= by) return from;
  const k = (d - by) / d;
  return { x: from.x + (tip.x - from.x) * k, y: from.y + (tip.y - from.y) * k };
}

/**
 * The leader from a box to where its arrow aims, drawn the way the inspectors
 * draw it: it leaves the centre of the box's bottom (or top) edge, drops
 * straight for a short stub, runs to a point straight above (or below) the
 * tip, and finishes straight down (or up) into the arrowhead — the head always
 * points square at the mark. The tip sits on the mark's near edge at the aim's
 * x, pushed out by `tipGap` so it clears the mark's colour band and never digs
 * in. Which side the box is on is judged against the mark's middle, so the
 * arrow never runs across the mark. Aiming at a free point, the tip is the
 * point itself. `clear` is false when the leader passes through another mark.
 */
export function routeLeader(rect: Rect, target: MarkPoly | null, point: Vec, space: CalloutSpace): LeaderRoute {
  const { marks, stub, tipGap, arrowLen } = space;
  const cy = rect.y + rect.h / 2;
  const ref = target ? target.mid : point;
  const side: LeaderSide = ref.y >= cy ? 'bottom' : 'top';
  const sign = side === 'bottom' ? 1 : -1;
  const s = leaderStart(rect, side);
  let tip = point;
  if (target) {
    const meet = edgePointAt(target, point.x, sign, 0.45 * arrowLen);
    tip = { x: meet.x, y: meet.y - sign * tipGap };
  }
  const dir = { x: 0, y: sign };
  const room = Math.max(0, (tip.y - s.y) * sign);
  const pts: Vec[] = [s];
  if (Math.abs(tip.x - s.x) > 0.5) {
    // the stub out of the box and the straight finish into the head (never shorter than `HEAD_RUN`), the run between them
    const land = Math.max(HEAD_RUN * arrowLen, Math.min(arrowLen + stub, 0.65 * room));
    const drop = Math.min(stub, 0.2 * room, Math.max(0, room - land));
    if (drop > 0.5) pts.push({ x: s.x, y: s.y + drop * sign });
    pts.push({ x: tip.x, y: tip.y - land * sign });
  }
  pts.push(tip);
  const clear = marks.every((mk) => {
    if (target && mk.id === target.id) return true;
    for (let i = 0; i + 1 < pts.length; i++) {
      const b = i + 2 === pts.length ? pullBack(pts[i]!, pts[i + 1]!, 0.5) : pts[i + 1]!;
      if (segmentCrossesPoly(pts[i]!, b, mk)) return false;
    }
    return true;
  });
  return { pts, tip, dir, side, face: null, clear };
}

/**
 * The leader to one of a mark's ends: the head comes in along the mark, square at the end.
 * It is how an inspector reaches a band that lies behind another one — round the end, never
 * across. From the stub the line runs straight to the landing beside the end; where that
 * would touch a mark (its own included) it heads out to the landing's column first and turns
 * down it sooner, until the way is clear.
 */
export function routeToEnd(rect: Rect, target: MarkPoly, y: number, face: MarkEnd, space: CalloutSpace): LeaderRoute {
  const { marks, stub, tipGap, arrowLen, u } = space;
  const d = face === 'right' ? -1 : 1; // the way the head points along x
  const meet = endPointAt(target, y, face);
  const tip = { x: meet.x - d * tipGap, y: meet.y };
  const side: LeaderSide = tip.y >= rect.y + rect.h / 2 ? 'bottom' : 'top';
  const sign = side === 'bottom' ? 1 : -1;
  const s = leaderStart(rect, side);
  const landing = { x: tip.x - d * Math.max(HEAD_RUN * arrowLen, arrowLen + stub), y: tip.y };
  const room = Math.max(0, (landing.y - s.y) * sign);
  const drop = Math.min(stub, 0.2 * room);
  const p1 = { x: s.x, y: s.y + drop * sign };
  const head = drop > 0.5 ? [s, p1] : [s];
  const isClear = (pts: Vec[]): boolean =>
    marks.every((mk) => {
      for (let i = 0; i + 1 < pts.length; i++) {
        const last = i + 2 === pts.length;
        if (last && mk.id === target.id) continue; // the head itself stops short of its mark
        if (segmentNearPoly(pts[i]!, last ? pullBack(pts[i]!, pts[i + 1]!, 0.5) : pts[i + 1]!, mk, 1.5 * u)) return false;
      }
      return true;
    });
  let first: Vec[] | null = null;
  for (const t of [1, 0.7, 0.4, 0.15, 0]) {
    const pts = t === 1 ? [...head, landing, tip] : [...head, { x: landing.x, y: p1.y + (landing.y - p1.y) * t }, landing, tip];
    first ??= pts;
    if (isClear(pts)) return { pts, tip, dir: { x: d, y: 0 }, side, face, clear: true };
  }
  return { pts: first!, tip, dir: { x: d, y: 0 }, side, face, clear: false };
}

/** How long a leader is. */
function routeLength(r: LeaderRoute): number {
  let l = 0;
  for (let i = 0; i + 1 < r.pts.length; i++) l += dist(r.pts[i]!, r.pts[i + 1]!);
  return l;
}

/* How near a leader may come: to a mark, to a box — and how far off their corners it goes round them. */
const MARK_CLEAR = 5.5,
  MARK_REACH = 7.5,
  BOX_CLEAR = 6,
  BOX_REACH = 8; // × u

const MARK_OBSTACLES = new WeakMap<number[], Map<string, Obstacle>>();
function markObstacle(mk: MarkPoly, clear: number, reach: number, u: number): Obstacle {
  let byKey = MARK_OBSTACLES.get(mk.pts);
  if (!byKey) MARK_OBSTACLES.set(mk.pts, (byKey = new Map()));
  const key = `${clear.toFixed(2)}|${reach.toFixed(2)}`;
  let o = byKey.get(key);
  if (!o) byKey.set(key, (o = polyObstacle(mk.pts, mk.bounds, clear, reach, Math.max(1, Math.min(u, 3)))));
  return o;
}

/** One way for an arrow to reach its mark: where the head ends and how it points. The line there is found after. */
interface Way {
  tip: Vec;
  dir: Vec;
  face: MarkEnd | null;
  /** The route as drawn when nothing is in the way (only the side facing the box has one). */
  plain: LeaderRoute | null;
  /** Counted against it when ways are compared: the side facing the box is what is wanted. */
  handicap: number;
}

/** Where a leader starts, lands and ends for one way of reaching its mark, leaving the box by one edge. */
interface Plan {
  s: Vec;
  p1: Vec;
  landing: Vec;
  way: Way;
  side: LeaderSide;
  plain: Vec[];
  /** A point it passes on its way to the landing (round the near end of a band, to its far side). */
  via?: Vec;
}

function planFrom(way: Way, rect: Rect, natural: LeaderSide, from: LeaderSide, space: CalloutSpace): Plan {
  const { stub, arrowLen } = space;
  const { tip, dir } = way;
  // the room between the box and the tip, the way the plain route measures it
  const near = leaderStart(rect, natural);
  const room = Math.max(0, (tip.y - near.y) * (natural === 'bottom' ? 1 : -1));
  const facing = way.plain !== null;
  // (the straight finish never shorter than `HEAD_RUN` heads)
  const land = Math.max(HEAD_RUN * arrowLen, facing && dir.y !== 0 ? Math.min(arrowLen + stub, 0.65 * room) : arrowLen + stub);
  const landing = { x: tip.x - dir.x * land, y: tip.y - dir.y * land };
  const s = leaderStart(rect, from);
  // leaving by the far edge of its box, the stub has all the room it likes
  const drop = from === natural ? Math.min(stub, facing ? Math.min(0.2 * room, Math.max(0, room - land)) : stub) : stub;
  return { s, p1: { x: s.x, y: s.y + drop * OUT[from].y }, landing, way, side: from, plain: from === natural && way.plain && way.plain.pts.length <= 4 ? way.plain.pts : [] };
}

/** Points that repeat (a stub or a landing of no length) told once. */
function tidy(pts: Vec[]): Vec[] {
  return pts.filter((p, i) => i === 0 || dist(p, pts[i - 1]!) > 0.5);
}

/** The angle (degrees, 0 … 90) at which a head pointing `dir` meets a mark's outline where its tip is. */
export function headAngle(mk: MarkPoly, tip: Vec, dir: Vec): number {
  const { pts } = mk;
  const n = pts.length / 2;
  const q = nearestOnPolyline(tip, pts, true);
  const i = q.seg,
    j = (i + 1) % n;
  const tx = pts[2 * j]! - pts[2 * i]!,
    ty = pts[2 * j + 1]! - pts[2 * i + 1]!;
  const tl = Math.hypot(tx, ty);
  if (tl < 1e-9) return 90;
  return (Math.asin(Math.min(1, Math.abs(dir.x * ty - dir.y * tx) / tl)) * 180) / Math.PI;
}

const AXES = new WeakMap<number[], Vec | null>();
/**
 * A long, thin mark's long axis (a band, a strip of ground): the direction its outline spreads
 * furthest, when it is at least about three and a half times as long as it is wide; null for any
 * other shape.
 */
export function longAxis(mk: MarkPoly): Vec | null {
  if (AXES.has(mk.pts)) return AXES.get(mk.pts)!;
  const ps = mk.samples;
  let mx = 0,
    my = 0;
  for (const p of ps) {
    mx += p.x;
    my += p.y;
  }
  mx /= ps.length || 1;
  my /= ps.length || 1;
  let a = 0,
    b = 0,
    c = 0;
  for (const p of ps) {
    a += (p.x - mx) * (p.x - mx);
    b += (p.x - mx) * (p.y - my);
    c += (p.y - my) * (p.y - my);
  }
  const half = (a - c) / 2;
  const root = Math.sqrt(half * half + b * b);
  const l1 = (a + c) / 2 + root,
    l2 = (a + c) / 2 - root;
  const theta = 0.5 * Math.atan2(2 * b, a - c);
  const axis = l2 <= 1e-9 || l1 / l2 >= 4 ? { x: Math.cos(theta), y: Math.sin(theta) } : null;
  AXES.set(mk.pts, axis);
  return axis;
}

/** Does a head pointing `dir` onto a mark at `tip` meet it well: at 45° or more to its outline, and across a band rather than along it? */
export function meetsWell(mk: MarkPoly, tip: Vec, dir: Vec): boolean {
  if (headAngle(mk, tip, dir) < 44.5) return false;
  const axis = longAxis(mk);
  return !axis || Math.abs(dir.x * axis.x + dir.y * axis.y) <= 0.75;
}

/** The outline's outward normal at a point on it (the side of the segment it lies on that is outside the mark). */
function outwardNormal(mk: MarkPoly, q: Vec, u: number): Vec | null {
  const { pts } = mk;
  const n = pts.length / 2;
  const near = nearestOnPolyline(q, pts, true);
  const i = near.seg,
    j = (i + 1) % n;
  const tx = pts[2 * j]! - pts[2 * i]!,
    ty = pts[2 * j + 1]! - pts[2 * i + 1]!;
  const tl = Math.hypot(tx, ty);
  if (tl < 1e-9) return null;
  const nn = { x: ty / tl, y: -tx / tl };
  return pointInPoly({ x: near.p.x + nn.x * u, y: near.p.y + nn.y * u }, pts) ? { x: -nn.x, y: -nn.y } : nn;
}

/**
 * Ways onto a mark square to its outline, from the side facing the box: the head comes in along the
 * outline's normal, so it meets the mark at right angles wherever the outline runs — across a band,
 * never along it (its ends are not offered). The nearest the mark's middle, facing the box most
 * squarely and with the box near, first; at most `count`, spread along the outline.
 */
const SQUARE = new WeakMap<number[], Map<string, { q: Vec; n: Vec; tip: Vec; landing: Vec }[]>>();
/** The places along a mark's outline a head may come in square to it (its ends left out, for a band): each sample's normal, tip and landing, its run in clear of the mark itself. */
function squareSpots(mk: MarkPoly, space: CalloutSpace): { q: Vec; n: Vec; tip: Vec; landing: Vec }[] {
  const { u, tipGap, arrowLen, stub } = space;
  let byKey = SQUARE.get(mk.pts);
  if (!byKey) SQUARE.set(mk.pts, (byKey = new Map()));
  const key = `${u}|${tipGap}|${arrowLen + stub}`;
  let spots = byKey.get(key);
  if (spots) return spots;
  spots = [];
  const axis = longAxis(mk);
  for (const q of mk.samples) {
    const n = outwardNormal(mk, q, u);
    if (!n) continue;
    if (axis && Math.abs(n.x * axis.x + n.y * axis.y) > 0.75) continue;
    const tip = { x: q.x + n.x * tipGap, y: q.y + n.y * tipGap };
    const landing = { x: tip.x + n.x * (arrowLen + stub), y: tip.y + n.y * (arrowLen + stub) };
    // the head's run in clear of the mark itself (a hollow of the outline), and well onto it — not where a corner or a rounded end lies nearer the tip than the side it was meant for
    if (segmentCrossesPoly(landing, { x: tip.x + n.x * 0.5, y: tip.y + n.y * 0.5 }, mk)) continue;
    if (headAngle(mk, tip, { x: -n.x, y: -n.y }) < 60) continue;
    spots.push({ q, n, tip, landing });
  }
  byKey.set(key, spots);
  return spots;
}

function squareWays(mk: MarkPoly, rect: Rect, space: CalloutSpace, count: number): Way[] {
  const { u, arrowLen, marks, stub } = space;
  const c = { x: rect.x + rect.w / 2, y: rect.y + rect.h / 2 };
  const found: { q: Vec; n: Vec; tip: Vec; score: number }[] = [];
  for (const { q, n, tip, landing } of squareSpots(mk, space)) {
    const tx = c.x - q.x,
      ty = c.y - q.y;
    const tl = Math.hypot(tx, ty) || 1;
    const facing = (n.x * tx + n.y * ty) / tl;
    if (facing < 0.2) continue;
    // the head's run in must be clear of every other mark
    const before = { x: tip.x + n.x * 0.5, y: tip.y + n.y * 0.5 };
    if (marks.some((m) => m.id !== mk.id && segmentCrossesPoly(landing, before, m))) continue;
    const nx = Math.max(rect.x, Math.min(rect.x + rect.w, landing.x)),
      ny = Math.max(rect.y, Math.min(rect.y + rect.h, landing.y));
    // (a landing level with the box can be reached from its top or bottom only by turning back: such come last)
    const level = landing.y > rect.y - 1.5 * stub && landing.y < rect.y + rect.h + 1.5 * stub ? 60 * u : 0;
    const score = dist(q, mk.mid) + 0.25 * Math.hypot(landing.x - nx, landing.y - ny) + (1 - facing) * 30 * u + level;
    found.push({ q, n, tip, score });
  }
  found.sort((a, b) => a.score - b.score);
  const out: Way[] = [];
  const best = found[0]?.score ?? 0;
  for (const f of found) {
    if (out.length >= count) break;
    if (out.some((w) => dist(w.tip, f.tip) < 20 * u)) continue;
    out.push({ tip: f.tip, dir: { x: -f.n.x || 0, y: -f.n.y || 0 }, face: null, plain: null, handicap: arrowLen + 0.5 * (f.score - best) });
  }
  return out;
}

/** The largest turn (degrees) at any corner of a polyline from its `from`th point on: near 180 is a hairpin. */
function sharpestTurn(pts: readonly Vec[], from = 1): number {
  let worst = 0;
  for (let i = from; i + 1 < pts.length; i++) {
    const a = pts[i - 1]!,
      b = pts[i]!,
      c = pts[i + 1]!;
    const d1x = b.x - a.x,
      d1y = b.y - a.y,
      d2x = c.x - b.x,
      d2y = c.y - b.y;
    const l1 = Math.hypot(d1x, d1y),
      l2 = Math.hypot(d2x, d2y);
    if (l1 < 0.5 || l2 < 0.5) continue;
    worst = Math.max(worst, (Math.acos(Math.max(-1, Math.min(1, (d1x * d2x + d1y * d2y) / (l1 * l2)))) * 180) / Math.PI);
  }
  return worst;
}

/** Whether a leader, once out of its box, comes back behind the edge it left by (alongside or over the box). */
function doublesBack(pts: readonly Vec[], side: LeaderSide, u: number): boolean {
  const s = pts[0]!;
  const out = OUT[side];
  for (let i = 1; i < pts.length; i++) if ((pts[i]!.x - s.x) * out.x + (pts[i]!.y - s.y) * out.y < -u) return true;
  return false;
}

/** A leader turned back on itself anywhere, hooked round off its stub, or come back past the edge of its box it left by. */
export function looksWrong(pts: readonly Vec[], side: LeaderSide, u: number): boolean {
  return sharpestTurn(pts) > 110 || sharpestTurn(pts.slice(0, 3)) > 95 || doublesBack(pts, side, u);
}

/**
 * Whether a leader, just out of its box, runs on along it — its first run after the stub within half
 * a stub of the edge it left by, under (or over) more than a quarter of the box — as it must when the
 * box sits so close to a mark level with it that there is no room to drop away from it.
 */
export function hugsBox(pts: readonly Vec[], rect: Rect, side: LeaderSide, stub: number): boolean {
  if (pts.length < 3) return false;
  const s = pts[0]!,
    p1 = pts[1]!,
    p2 = pts[2]!;
  const off = (p: Vec) => (p.y - s.y) * OUT[side].y;
  if (off(p1) > 0.5 * stub || off(p2) > 0.6 * stub) return false;
  return Math.min(Math.max(p1.x, p2.x), rect.x + rect.w) - Math.max(Math.min(p1.x, p2.x), rect.x) > 0.25 * rect.w;
}

/** How much of a leader (bar its last `finish`, the run into its head) runs within `near` of a mark's outline, in pieces `step` long. */
function alongMarks(pts: readonly Vec[], marks: readonly MarkPoly[], near: number, finish: number, step: number, u: number): number {
  let total = 0;
  for (let i = 0; i + 1 < pts.length; i++) total += dist(pts[i]!, pts[i + 1]!);
  const upTo = total - finish;
  if (upTo <= 0) return 0;
  // each mark as a border kept `near` clear (cached): a piece it blocks runs alongside it, or across it
  const walls = marks.map((mk) => markObstacle(mk, near, near + u, u));
  let along = 0,
    walked = 0;
  for (let i = 0; i + 1 < pts.length && walked < upTo; i++) {
    const a = pts[i]!,
      b = pts[i + 1]!;
    const l = dist(a, b);
    if (l < 1e-6) continue;
    const box = { x: Math.min(a.x, b.x) - near, y: Math.min(a.y, b.y) - near, w: Math.abs(b.x - a.x) + 2 * near, h: Math.abs(b.y - a.y) + 2 * near };
    const close = walls.filter((w) => rectsOverlap(box, w.bounds));
    if (!close.length) {
      walked += l;
      continue;
    }
    for (let t = 0; t < l && walked < upTo; t += step, walked += step) {
      const p = { x: a.x + ((b.x - a.x) * t) / l, y: a.y + ((b.y - a.y) * t) / l };
      const k = Math.min(l, t + step);
      const q = { x: a.x + ((b.x - a.x) * k) / l, y: a.y + ((b.y - a.y) * k) / l };
      if (blockedBy(p, q, close) >= 0) along += k - t;
    }
  }
  return along;
}

/**
 * One arrow's leader. It never runs through a mark, a callout or a note, and crosses another
 * leader only when there is no other way at all.
 *
 * Where the person put the tip, it stays. Otherwise the arrow points at the centre of its mark's
 * top edge or of its bottom edge — always, whatever is moved about: the one facing the box when
 * the plain leader (stub, run, straight finish) is clear; else whichever of the two the shortest
 * clear line reaches, found round everything in the way (route.ts). Only if neither can be
 * reached does it come in at an end of its mark. It leaves the box by the edge facing its mark;
 * boxed in there, by the opposite edge and round its own box; squeezed everywhere, it passes
 * closer to things than it likes before it would ever cross one.
 *
 * Every arrow must look right (2026-10-24, Adam): its head meets the outline at 45° or more and
 * across a band, never along it — where a straight drop onto the top or bottom edge would not (a
 * steep or upright band), it comes in square to the outline on the side facing the box instead;
 * it leaves the box by the edge facing where it goes (a side, for a mark level with the box) and
 * never comes back past that edge; it never turns back on itself; it keeps off running alongside a
 * mark; and its head keeps its distance from every box.
 */
export function routeAim(rect: Rect, aim: LeaderAim, space: CalloutSpace, around: Around = NOTHING, thorough = true): LeaderRoute {
  // what is far from both the box and its mark plays no part in the way between them: the boxes and
  // leaders in the corridor round the two (as wide again as the larger of them, and more) — and a way
  // found once, for the same box, mark and what is round them, is that way again (the plan is laid out
  // at every change: typing a callout's words on a crowded map routed every other arrow at every letter)
  const target = aim.mark ? aim.mark.bounds : { x: aim.point.x, y: aim.point.y, w: 0, h: 0 };
  const x0 = Math.min(rect.x, target.x),
    y0 = Math.min(rect.y, target.y);
  const x1 = Math.max(rect.x + rect.w, target.x + target.w),
    y1 = Math.max(rect.y + rect.h, target.y + target.h);
  const pad = Math.max(x1 - x0, y1 - y0) / 2 + 40 * space.u;
  const zone = { x: x0 - pad, y: y0 - pad, w: x1 - x0 + 2 * pad, h: y1 - y0 + 2 * pad };
  const boxes = around.boxes.filter((r) => rectsOverlap(r, zone));
  const leaders = around.leaders.filter((l) => l.length > 0 && rectsOverlap(polyBoundsOf(l), zone));
  const n = (v: number) => Math.round(v * 100) / 100;
  const key = [
    spaceKey(space),
    `${n(rect.x)},${n(rect.y)},${n(rect.w)},${n(rect.h)}`,
    `${aim.mark?.id ?? ''}@${n(aim.point.x)},${n(aim.point.y)}|${aim.pinned ? 1 : 0}|${aim.face ?? ''}|${aim.arrow ?? ''}|${aim.off ? 1 : 0}`,
    boxes.map((r) => `${n(r.x)},${n(r.y)},${n(r.w)},${n(r.h)}`).join(';'),
    leaders.map((l) => l.map((p) => `${n(p.x)},${n(p.y)}`).join(' ')).join(';'),
    thorough ? 1 : 0,
  ].join('#');
  let got = ROUTES.get(key);
  if (got) ROUTES.delete(key);
  else {
    got = routeAimAfresh(rect, aim, space, { boxes, leaders }, thorough);
    if (ROUTES.size >= ROUTES_MAX) ROUTES.delete(ROUTES.keys().next().value!);
  }
  ROUTES.set(key, got);
  return copyRoute(got);
}
/** A way handed out as its own: what is remembered is never changed through it. */
function copyRoute(r: LeaderRoute): LeaderRoute {
  return { ...r, pts: r.pts.map((p) => ({ ...p })), tip: { ...r.tip }, dir: { ...r.dir }, ...(r.radii ? { radii: r.radii.slice() } : {}) };
}
/** The ways found lately, by all that decides one (`routeAim`). */
const ROUTES = new Map<string, LeaderRoute>();
const ROUTES_MAX = 512;
/**
 * What of a callout space decides a way — its marks, the plan, its measures — as a short name, the
 * same for every space that says the same (said once for each space, looked up by what it says).
 */
const SPACE_KEYS = new WeakMap<CalloutSpace, string>();
const SPACE_NAMES = new Map<string, string>();
/** (never one name twice: a name forgotten is not given to another space, whose ways would be taken for its) */
let spaceSeq = 0;
function spaceKey(space: CalloutSpace): string {
  let k = SPACE_KEYS.get(space);
  if (k === undefined) {
    const n = (v: number) => Math.round(v * 100) / 100;
    const said = [space.marks.map((m) => `${m.id}:${m.pts.map(n).join(',')}`).join('/'), `${n(space.bounds.x)},${n(space.bounds.y)},${n(space.bounds.w)},${n(space.bounds.h)}`, space.u, space.minGap, space.arrowLen, space.stub, space.tipGap].join('|');
    k = SPACE_NAMES.get(said);
    if (k === undefined) {
      if (SPACE_NAMES.size >= 256) SPACE_NAMES.clear();
      k = `s${++spaceSeq}`;
      SPACE_NAMES.set(said, k);
    }
    SPACE_KEYS.set(space, k);
  }
  return k;
}
function polyBoundsOf(l: readonly Vec[]): Rect {
  let x0 = Infinity,
    y0 = Infinity,
    x1 = -Infinity,
    y1 = -Infinity;
  for (const p of l) {
    x0 = Math.min(x0, p.x);
    y0 = Math.min(y0, p.y);
    x1 = Math.max(x1, p.x);
    y1 = Math.max(y1, p.y);
  }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}
function routeAimAfresh(rect: Rect, aim: LeaderAim, space: CalloutSpace, around: Around, thorough: boolean): LeaderRoute {
  const mk = aim.mark;
  const { u, arrowLen, tipGap, marks } = space;
  const c = { x: rect.x + rect.w / 2, y: rect.y + rect.h / 2 };
  const near = mk && aim.face ? routeToEnd(rect, mk, aim.point.y, aim.face, space) : routeLeader(rect, mk, aim.point, space);
  let natural = near.side;
  let first: Way | null = { tip: near.tip, dir: near.dir, face: near.face, plain: near, handicap: 0 };
  const good = (w: Way) => !mk || w.face !== null || meetsWell(mk, w.tip, w.dir);
  // a tip put by hand on the far side of a band: round the band's end nearer the box (the same end
  // wherever along that side the tip goes, so a tip slid along it never flips the leader from one end to the other)
  let via: Vec | null = null;
  // where the head may not come in square (another mark or a box in the way of its run in), a little turned: as little as will do, never past 40°
  const turned: Way[] = [];
  if (mk && aim.pinned && !aim.face) {
    // where the person put it, anywhere on the outline, it stays — the head square to the outline there, pointing in
    const q = nearestOnPolyline(aim.point, mk.pts, true).p;
    const n = outwardNormal(mk, q, u);
    if (n) {
      const tip = { x: q.x + n.x * tipGap, y: q.y + n.y * tipGap };
      const land = Math.max(HEAD_RUN * arrowLen, arrowLen + space.stub);
      first = { tip, dir: { x: -n.x || 0, y: -n.y || 0 }, face: null, plain: null, handicap: 0 };
      natural = q.y + n.y * land >= c.y ? 'bottom' : 'top';
      const axis = longAxis(mk);
      // (on the far side of a band, not on an end: round the nearer end, to beyond it level with the landing)
      if (axis && Math.abs(n.x * axis.x + n.y * axis.y) < 0.5 && n.x * (c.x - q.x) + n.y * (c.y - q.y) < 0) {
        const end = pastNearEnd(mk, axis, c, MARK_REACH * u + space.stub);
        const side = n.x * -axis.y + n.y * axis.x >= 0 ? 1 : -1;
        const off = nearestOnPolyline(mk.mid, mk.pts, true).d + tipGap + land;
        via = { x: end.x - axis.y * side * off, y: end.y + axis.x * side * off };
      }
      for (const deg of [15, -15, 30, -30, 40, -40]) {
        const a = (deg * Math.PI) / 180;
        const d = { x: -(n.x * Math.cos(a) - n.y * Math.sin(a)), y: -(n.x * Math.sin(a) + n.y * Math.cos(a)) };
        turned.push({ tip, dir: d, face: null, plain: null, handicap: Math.abs(deg) * arrowLen * 0.1 });
      }
      // and, where coming round to it square would hook or turn back (a tip on an end, or a corner by the box),
      // straight at it from the stub — the head on the line's own axis, meeting the outline at 30° or more
      for (const e of ['top', 'bottom'] as const) {
        const s0 = leaderStart(rect, e);
        const p = { x: s0.x, y: s0.y + OUT[e].y * space.stub };
        const l = dist(p, tip);
        if (l < 1e-6) continue;
        const d = { x: (tip.x - p.x) / l, y: (tip.y - p.y) / l };
        if ((d.y * OUT[e].y) <= 0 || headAngle(mk, tip, d) < 30 || segmentCrossesPoly(p, { x: tip.x - d.x * 0.5, y: tip.y - d.y * 0.5 }, mk)) continue;
        turned.push({ tip, dir: d, face: null, plain: null, handicap: 8 * arrowLen });
      }
    }
  } else if (mk && !good(first)) first = null;
  // the ways to try, in turn: what is wanted (and ways square onto the mark's facing side); then (left
  // to itself) the far side's centre; then an end
  // (the ways square onto the mark are found only once the straight drop has not done)
  const head: Way[] = first ? [first] : [];
  const tiers: (Way[] | (() => Way[]))[] = [head];
  if (turned.length) tiers.push(turned);
  if (mk && !aim.pinned && !aim.face) {
    const sign = near.dir.y;
    const meet = edgePointAt(mk, aim.point.x, -sign, 0.45 * arrowLen);
    const far: Way = { tip: { x: meet.x, y: meet.y + sign * tipGap }, dir: { x: 0, y: -sign }, face: null, plain: null, handicap: 2 * arrowLen };
    // (weighing a box's place, as before: the far side's centre beside the drop, ways square onto the mark only where the drop meets it badly)
    if (thorough) tiers.push(() => [...squareWays(mk, rect, space, 3), ...(good(far) ? [far] : [])]);
    else if (first) head.push(...(good(far) ? [far] : []));
    else tiers.push(() => [...squareWays(mk, rect, space, 1), ...(good(far) ? [far] : [])]);
    tiers.push(
      (['right', 'left'] as const)
        .map((face): Way => {
          const r = routeToEnd(rect, mk, endMiddle(mk, face), face, space);
          return { tip: r.tip, dir: r.dir, face, plain: null, handicap: 3 * arrowLen };
        })
        // an end of a band is along it: not a way
        .filter((w) => !longAxis(mk) || Math.abs(w.dir.x * longAxis(mk)!.x + w.dir.y * longAxis(mk)!.y) <= 0.75),
    );
  }
  const lines = around.leaders.map(lineObstacle);
  const others = marks.filter((m) => !mk || m.id !== mk.id);
  // the head's wings and their white keep further off a box than the line does
  const wings = 0.4 * arrowLen;

  const solve = (plan: Plan, slim: number, bound: number): { pts: Vec[]; cost: number; hard: Obstacle[]; plain: boolean } | null => {
    const { way } = plan;
    // what every way must keep clear of; the leader's own mark lets the head come up to it, its own box lets the stub leave
    const otherMarks = others.map((m) => markObstacle(m, MARK_CLEAR * u * slim, MARK_REACH * u * slim, u));
    const boxes = around.boxes.map((r) => rectObstacle(r, BOX_CLEAR * u * slim, BOX_REACH * u * slim));
    const headBoxes = around.boxes.map((r) => rectObstacle(r, BOX_CLEAR * u * slim + wings, BOX_REACH * u * slim + wings));
    const gapToOwn = mk ? Math.max(0.5, Math.min(MARK_CLEAR * u * slim, 0.8 * dist(plan.landing, way.tip))) : 0;
    const ownMark = mk ? [markObstacle(mk, gapToOwn, Math.max(MARK_REACH * u * slim, gapToOwn + u), u)] : [];
    const ownClear = Math.max(-0.5, Math.min(BOX_CLEAR * u * slim, 0.8 * dist(plan.s, plan.p1)));
    const ownBox = rectObstacle(rect, ownClear, Math.max(BOX_REACH * u * slim, ownClear + u));
    const hard = [...otherMarks, ...boxes];
    // the head itself stops short of its own mark, nothing of the mark lies before it, and its wings keep off every box
    const beforeTip = { x: way.tip.x - way.dir.x * 0.5, y: way.tip.y - way.dir.y * 0.5 };
    const headClear = (from: Vec, soft: Obstacle[]): boolean => !(mk && segmentCrossesPoly(from, beforeTip, mk)) && pathClear([from, beforeTip], [...otherMarks, ...headBoxes, ...soft]);
    // first with the other leaders as walls; if there is no way at all, across them (never across a mark or a box)
    for (const soft of lines.length ? [lines, []] : [[]]) {
      const all = [...hard, ...ownMark, ownBox, ...soft];
      const runClear = (pts: Vec[]): boolean =>
        pts.every((p, i) => {
          if (i + 1 >= pts.length) return true;
          if (i + 2 === pts.length) return headClear(p, soft) && (i === 0 || pathClear([p, beforeTip], [ownBox]));
          return pathClear([p, pts[i + 1]!], i === 0 ? [...hard, ...ownMark, ...soft] : all);
        });
      let pts: Vec[] | null = null;
      const plain = plan.plain.length > 0 && runClear(plan.plain);
      if (plain) pts = plan.plain;
      else if (pathClear([plan.s, plan.p1], [...hard, ...ownMark, ...soft]) && headClear(plan.landing, soft)) {
        if (plan.via) {
          // round the near end of the band: to beyond its end, then on to the landing on its far side
          const out = findPath(plan.p1, plan.via, all, space.bounds);
          const back = out && findPath(plan.via, plan.landing, all, space.bounds);
          if (out && back) pts = tidy([plan.s, ...out, ...back.slice(1), way.tip]);
        } else {
          const found = findPath(plan.p1, plan.landing, all, space.bounds);
          if (found) pts = tidy([plan.s, ...found, way.tip]);
        }
      }
      if (!pts) continue;
      let cost = way.handicap + (soft.length === 0 && lines.length ? 40 * arrowLen : 0);
      for (let i = 0; i + 1 < pts.length; i++) cost += dist(pts[i]!, pts[i + 1]!);
      // a leader that turns back on itself, hooks round off its stub, or comes back past the edge it left by, is a last resort
      if (looksWrong(pts, plan.side, u)) cost += 60 * arrowLen;
      // and one that runs on right along its box, out of it, nearly so
      else if (hugsBox(pts, rect, plan.side, space.stub)) cost += 20 * arrowLen;
      // running alongside a mark costs as much again as the run itself (not worth weighing for a way already beaten)
      if (thorough && cost < bound) cost += alongMarks(pts, marks, 10 * u, arrowLen + space.stub + 8 * u, 3 * u, u);
      return { pts, cost, hard: [...hard, ...ownMark, ownBox], plain };
    }
    return null;
  };

  /**
   * The ways out of the box for a way in: by the top or bottom edge facing the mark, then the far one
   * — first those whose landing lies beyond the end of their stub, so the leader never turns back past
   * the edge it left by; boxed in there, round its own box after all, a last resort (`looksWrong`).
   */
  const plansFor = (way: Way): Plan[] => {
    const plans = [natural, natural === 'bottom' ? 'top' : 'bottom'].map((e) => ({ ...planFrom(way, rect, natural, e as LeaderSide, space), ...(via && (way === first || turned.includes(way)) ? { via } : {}) }));
    const facing = (p: Plan) => (p.landing.y - p.p1.y) * OUT[p.side].y >= 0;
    const ok = plans.filter(facing);
    if (!thorough) return ok.length ? ok.slice(0, 1) : plans.slice(0, 1);
    return [...ok, ...plans.filter((p) => !facing(p))];
  };

  let best: { plan: Plan; pts: Vec[]; cost: number; hard: Obstacle[]; plain: boolean } | null = null;
  // what a route costs that nothing went wrong with: a better one cannot be had by looking further
  const clean = (r: { cost: number; plain: boolean }, way: Way) => r.plain && way === first && r.cost < 40 * arrowLen;
  const found = new Map<number, Way[]>();
  search: for (const slim of thorough ? [1, 0.35] : [1]) {
    for (const [k, tier] of tiers.entries()) {
      let ways = found.get(k);
      if (!ways) found.set(k, (ways = typeof tier === 'function' ? tier() : tier));
      for (const way of ways) {
        for (const plan of plansFor(way)) {
          const r = solve(plan, slim, (best as { cost: number } | null)?.cost ?? Infinity);
          if (r && (!best || r.cost < best.cost)) best = { plan, ...r };
          // nothing in the way of the plain leader, and it looks right: that is the leader, no need to look further
          if (r && clean(r, way) && !looksWrong(r.pts, plan.side, u) && !hugsBox(r.pts, rect, plan.side, space.stub)) break search;
        }
      }
      // a good one found (nothing crossed, nothing turned back): no need to look round the far edge or an end
      const sofar = best as { cost: number } | null;
      if (sofar && sofar.cost < 40 * arrowLen) break search;
    }
    // any way at all with the room it likes is better than squeezing past things
    if (best) break;
  }
  // no way at all, however squeezed: the plain leader (stub, run, the straight finish into its head), and it says so
  if (!best) {
    if (!first) return { ...near, clear: false };
    // (a tip put by hand: straight at it from the stub, the head on the line, when that meets the outline well enough)
    const straight = turned.find((w) => w.handicap === 8 * arrowLen);
    const way = straight ?? first;
    const plan = planFrom(way, rect, natural, natural, space);
    // (to a band's far side, round its near end even so)
    const pts = tidy([plan.s, plan.p1, ...(via && !straight ? [via] : []), plan.landing, way.tip]);
    if (straight) return { pts, tip: way.tip, dir: way.dir, side: natural, face: null, clear: false, radii: pts.map(() => 0) };
    return { pts, tip: first.tip, dir: first.dir, side: natural, face: first.face, clear: false, radii: pts.map(() => 0) };
  }
  const { plan, pts, hard } = best;
  const { way } = plan;
  return { pts, tip: way.tip, dir: way.dir, side: plan.side, face: way.face, clear: true, ...(best.plain ? {} : { detour: true }), radii: easedRadii(pts, 1.875 * arrowLen, HEAD_RUN * arrowLen, hard) };
}

/**
 * A point just past the end of a band nearer to `from` (the box), on its long axis: a leader going to
 * the band's far side passes it, round that end.
 */
function pastNearEnd(mk: MarkPoly, axis: Vec, from: Vec, beyond: number): Vec {
  let lo = Infinity,
    hi = -Infinity;
  const m = mk.mid;
  for (const p of mk.samples) {
    const t = (p.x - m.x) * axis.x + (p.y - m.y) * axis.y;
    lo = Math.min(lo, t);
    hi = Math.max(hi, t);
  }
  const at = (from.x - m.x) * axis.x + (from.y - m.y) * axis.y;
  const t = at >= (lo + hi) / 2 ? hi + beyond : lo - beyond;
  return { x: m.x + axis.x * t, y: m.y + axis.y * t };
}

/**
 * How far each corner of a leader may be eased into an arc: up to `radius`, never more than half
 * a run, never into the straight finish the head needs — and only as far as the arc itself stays
 * clear of the obstacles (the line was found clear; an arc cuts its corners).
 */
export function easedRadii(pts: readonly Vec[], radius: number, finish: number, obstacles: readonly Obstacle[] = []): number[] {
  const n = pts.length;
  const radii = new Array<number>(n).fill(0);
  for (let i = 1; i + 1 < n; i++) {
    const p0 = pts[i - 1]!,
      p1 = pts[i]!,
      p2 = pts[i + 1]!;
    const a = dist(p0, p1),
      b = dist(p2, p1);
    if (a < 1e-6 || b < 1e-6) continue;
    const u0 = { x: (p0.x - p1.x) / a, y: (p0.y - p1.y) / a },
      u2 = { x: (p2.x - p1.x) / b, y: (p2.y - p1.y) / b };
    const half = Math.acos(Math.max(-1, Math.min(1, u0.x * u2.x + u0.y * u2.y))) / 2; // of the angle between the two runs
    const tan = Math.tan(half);
    if (!Number.isFinite(tan) || tan > 1e6 || half < 1e-3) continue;
    const roomFor = Math.min(a / 2, i + 2 === n ? Math.max(0, b - finish) : b / 2);
    let r = Math.min(radius, roomFor * tan);
    const bis = { x: u0.x + u2.x, y: u0.y + u2.y };
    const bl = Math.hypot(bis.x, bis.y);
    while (r > 0.5 && obstacles.length && bl > 1e-6) {
      const t = r / tan;
      const c = r / Math.sin(half);
      const t0 = { x: p1.x + u0.x * t, y: p1.y + u0.y * t },
        t2 = { x: p1.x + u2.x * t, y: p1.y + u2.y * t },
        mid = { x: p1.x + (bis.x / bl) * (c - r), y: p1.y + (bis.y / bl) * (c - r) };
      if (pathClear([t0, mid, t2], obstacles)) break;
      r /= 2;
    }
    radii[i] = r > 0.5 ? r : 0;
  }
  return radii;
}

/** A segment passing through a polygon, or within `margin` of it on either hand. */
function segmentNearPoly(a: Vec, b: Vec, mk: { pts: number[]; bounds: Rect }, margin: number): boolean {
  if (segmentCrossesPoly(a, b, mk)) return true;
  const l = dist(a, b);
  if (l < 1e-6 || margin <= 0) return false;
  const nx = (-(b.y - a.y) / l) * margin,
    ny = ((b.x - a.x) / l) * margin;
  return segmentCrossesPoly({ x: a.x + nx, y: a.y + ny }, { x: b.x + nx, y: b.y + ny }, mk) || segmentCrossesPoly({ x: a.x - nx, y: a.y - ny }, { x: b.x - nx, y: b.y - ny }, mk);
}

/** The height of the middle of a mark's left or right end. */
export function endMiddle(mk: MarkPoly, face: MarkEnd): number {
  const { bounds, samples } = mk;
  const reach = Math.max(2, Math.min(0.5 * bounds.h, 0.1 * bounds.w));
  const edge = face === 'right' ? bounds.x + bounds.w : bounds.x;
  let lo = Infinity,
    hi = -Infinity;
  for (const p of samples) {
    if (Math.abs(p.x - edge) > reach) continue;
    lo = Math.min(lo, p.y);
    hi = Math.max(hi, p.y);
  }
  return lo === Infinity ? bounds.y + bounds.h / 2 : (lo + hi) / 2;
}

/**
 * The outermost point of a mark's outline at height y on its left or right end (y is brought
 * into the mark's span, a little in from the corners so the head sits on the end, not off it).
 */
export function endPointAt(mk: MarkPoly, y: number, face: MarkEnd): Vec {
  const { pts, bounds } = mk;
  const n = pts.length / 2;
  const inset = Math.min(2, bounds.h / 4);
  const py = Math.max(bounds.y + inset, Math.min(bounds.y + bounds.h - inset, y));
  let best: number | null = null;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const ax = pts[2 * i]!,
      ay = pts[2 * i + 1]!,
      bx = pts[2 * j]!,
      by = pts[2 * j + 1]!;
    if (!((ay <= py && by >= py) || (by <= py && ay >= py)) || ay === by) continue;
    const x = ax + ((py - ay) * (bx - ax)) / (by - ay);
    if (best === null || (face === 'right' ? x > best : x < best)) best = x;
  }
  return { x: best ?? (face === 'right' ? bounds.x + bounds.w : bounds.x), y: py };
}

/**
 * The point of a mark's outline that an arrow coming down (sign +1) or up (−1) at the given x
 * meets first: the topmost (or bottommost) edge there, so nothing of the mark lies between the
 * arrow and its tip — it can never run through its own mark. Off the mark's span, x is brought
 * in to the span, `inset` from its ends (half an arrowhead: the head sits on the edge, not off the corner).
 */
export function edgePointAt(mk: MarkPoly, x: number, sign: number, inset = 2): Vec {
  const { pts, bounds, mid } = mk;
  const hitAt = (px: number): Vec | null => {
    const ys = crossingsAt(pts, px);
    return ys.length ? { x: px, y: sign > 0 ? ys[0]! : ys[ys.length - 1]! } : null;
  };
  const keepIn = Math.min(inset, bounds.w / 4);
  const cx = Math.max(bounds.x + keepIn, Math.min(bounds.x + bounds.w - keepIn, x));
  const direct = hitAt(cx);
  if (direct) return direct;
  // no edge exactly there (an upright side at that very x): the nearest x with one, either way
  const step = Math.max(0.5, bounds.w / 400);
  for (let d = step; d <= bounds.w; d += step) {
    const r = cx + d <= bounds.x + bounds.w ? hitAt(cx + d) : null;
    if (r) return r;
    const l = cx - d >= bounds.x ? hitAt(cx - d) : null;
    if (l) return l;
  }
  return outlineToward(pts, mid, -sign) ?? nearestOnPolyline(mid, pts, true).p;
}

/** The outline point nearest to p among those lying towards `dy` (−1 = above p, +1 = below), if any. */
function outlineToward(pts: number[], p: Vec, dy: number): Vec | null {
  const n = pts.length / 2;
  let best: Vec | null = null;
  let bd = Infinity;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const a = { x: pts[2 * i]!, y: pts[2 * i + 1]! };
    const b = { x: pts[2 * j]!, y: pts[2 * j + 1]! };
    const q = nearestOnSegment(p, a, b);
    if ((q.p.y - p.y) * dy <= 0.5) continue;
    if (q.d < bd) {
      bd = q.d;
      best = q.p;
    }
  }
  return best;
}

/* ------------------------------------------------------------- validity */

/** The marks a callout points at: one, several, or none. */
export type OwnMarks = MarkPoly | readonly (MarkPoly | null)[] | null;
function isOwn(own: OwnMarks, mk: MarkPoly): boolean {
  if (!own) return false;
  return 'id' in own ? own.id === mk.id : own.some((o) => o !== null && o.id === mk.id);
}

/**
 * May a box sit here: inside the image, off every mark — with room between for the mark's colour
 * band and the box's own border and white casing (`BOX_OFF`) — and far enough from its own marks
 * for the arrowheads.
 */
export function boxAllowed(rect: Rect, target: OwnMarks, space: CalloutSpace): boolean {
  const { bounds, u, minGap } = space;
  const margin = u;
  if (rect.x < bounds.x + margin || rect.y < bounds.y + margin || rect.x + rect.w > bounds.x + bounds.w - margin || rect.y + rect.h > bounds.y + bounds.h - margin) return false;
  for (const mk of space.marks) {
    const own = isOwn(target, mk);
    if (rectOverlapsPoly(own ? inflateXY(rect, 0.5 * minGap, minGap) : inflateXY(rect, BOX_OFF * u, BOX_OFF * u), mk)) return false;
  }
  return true;
}
/** How far a box keeps off a mark that is not its own (× u): the band (2u), half the box's border and its casing (2.1u), and a hair. */
const BOX_OFF = 4.5;

export function boxValid(rect: Rect, target: MarkPoly | null, point: Vec, space: CalloutSpace): { ok: boolean; route: LeaderRoute } {
  const route = routeLeader(rect, target, point, space);
  return { ok: route.clear && boxAllowed(rect, target, space), route };
}

/** May a box sit here with no other box under it: allowed, and clear of `avoid` (other callouts and notes) by a hair. */
export function boxFree(rect: Rect, target: OwnMarks, space: CalloutSpace, avoid: readonly Rect[]): boolean {
  if (!boxAllowed(rect, target, space)) return false;
  const r = inflateXY(rect, space.u, space.u);
  return !avoid.some((o) => rectsOverlap(r, o));
}

/**
 * The nearest place to `rect` where the box may rest (see `boxFree`): looked for in widening
 * rings round where it is, then brought back towards it as far as it may come. Where a carried
 * box is let go over a mark or another callout, this is where it settles. Null when there is
 * nowhere within `reach`.
 */
export function nearestFree(rect: Rect, own: OwnMarks, space: CalloutSpace, avoid: readonly Rect[], reach = 400 * space.u): Vec | null {
  return nearestWhere(rect, (at) => boxFree(at, own, space, avoid), 4 * space.u, reach);
}

/**
 * The nearest place to `rect` where `rests` holds: looked for in widening rings round where it is,
 * each place found brought back towards it as far as it may come. Null when there is nowhere
 * within `reach`.
 */
export function nearestWhere(rect: Rect, rests: (at: Rect) => boolean, step: number, reach: number): Vec | null {
  const free = (x: number, y: number) => rests({ x, y, w: rect.w, h: rect.h });
  if (free(rect.x, rect.y)) return { x: rect.x, y: rect.y };
  for (let r = step; r <= reach; r += step) {
    const n = Math.max(16, Math.ceil((2 * Math.PI * r) / (1.5 * step)));
    // every place on this ring where it may rest, each brought back towards where it was let go as
    // far as it may come: the nearest of those
    let best: Vec | null = null;
    let bd = Infinity;
    let tried = 0;
    for (let k = 0; k < n && tried < 24; k++) {
      const a = (2 * Math.PI * k) / n;
      const x = rect.x + Math.cos(a) * r,
        y = rect.y + Math.sin(a) * r;
      if (!free(x, y)) continue;
      tried++;
      let lo = 0,
        hi = 1;
      for (let i = 0; i < 10; i++) {
        const t = (lo + hi) / 2;
        if (free(rect.x + (x - rect.x) * t, rect.y + (y - rect.y) * t)) hi = t;
        else lo = t;
      }
      if (hi * r < bd) {
        bd = hi * r;
        best = { x: rect.x + (x - rect.x) * hi, y: rect.y + (y - rect.y) * hi };
      }
    }
    if (best) return best;
  }
  return null;
}

/* ------------------------------------------------------------ placement */

export interface PlaceOptions {
  /** Boxes already on the plan (other callouts, notes): overlapping them is costly, and leaders go round them. */
  avoid: Rect[];
  /** Leaders already drawn: a box does not sit on one, and new leaders do not cross them. */
  leaders?: readonly (readonly Vec[])[];
  /** Where this box was last time (hysteresis). */
  prev: Rect | null;
}

interface Candidate {
  rect: Rect;
  cost: number;
}

/**
 * Automatic placement: directly above (preferred) or below what the callout points at, close
 * to it, arrows near-vertical, clear of other boxes, steady over time. A callout with several
 * arrows settles over the middle of its marks, where every arrow reaches its mark cleanly.
 */
/**
 * The last boxes placed, by everything that decides where one goes: placing one is costly (its arrows
 * routed from every spot it tries — a second, for a label with arrows to a few long outlines), and the
 * plan is laid out again at every change, most of which leave a box's question as it was (2026-10-28:
 * the fuzz found typing a callout's words on a crowded soils map taking 1.4 s a letter).
 */
const PLACED = new Map<string, { rect: Rect; routes: LeaderRoute[] }>();
const PLACED_MAX = 64;
function placeKey(size: { w: number; h: number }, aims: readonly LeaderAim[], space: CalloutSpace, opts: PlaceOptions): string {
  const n = (v: number) => Math.round(v * 100) / 100;
  const r = (q: Rect) => `${n(q.x)},${n(q.y)},${n(q.w)},${n(q.h)}`;
  const line = (l: readonly Vec[]) => l.map((p) => `${n(p.x)},${n(p.y)}`).join(' ');
  return [
    `${n(size.w)}x${n(size.h)}`,
    aims.map((a) => `${a.mark?.id ?? ''}@${n(a.point.x)},${n(a.point.y)}|${a.pinned ? 1 : 0}|${a.face ?? ''}|${a.arrow ?? ''}|${a.off ? 1 : 0}`).join(';'),
    spaceKey(space),
    opts.avoid.map(r).join(';'),
    (opts.leaders ?? []).map(line).join(';'),
    opts.prev ? r(opts.prev) : '-',
  ].join('#');
}
export function placeCallout(size: { w: number; h: number }, aims: readonly LeaderAim[], space: CalloutSpace, opts: PlaceOptions): { rect: Rect; routes: LeaderRoute[] } {
  const key = placeKey(size, aims, space, opts);
  let got = PLACED.get(key);
  if (got) {
    // (the most recent last: the oldest goes first)
    PLACED.delete(key);
  } else {
    got = placeCalloutAfresh(size, aims, space, opts);
    if (PLACED.size >= PLACED_MAX) PLACED.delete(PLACED.keys().next().value!);
  }
  PLACED.set(key, got);
  return { rect: { ...got.rect }, routes: got.routes.map(copyRoute) };
}
function placeCalloutAfresh(size: { w: number; h: number }, aims: readonly LeaderAim[], space: CalloutSpace, opts: PlaceOptions): { rect: Rect; routes: LeaderRoute[] } {
  const { u, minGap, bounds } = space;
  const { w, h } = size;
  const own = aims.map((a) => a.mark);
  const anchors = aims.map((a) => (a.mark && !pointInPoly(a.point, a.mark.pts) ? nearestOnPolyline(a.point, a.mark.pts, true).p : a.point));
  const anchor = { x: anchors.reduce((t, p) => t + p.x, 0) / anchors.length, y: anchors.reduce((t, p) => t + p.y, 0) / anchors.length };
  const marks = own.filter((mk): mk is MarkPoly => mk !== null);
  const drawn = opts.leaders ?? [];
  // every arrow keeps clear of the boxes and leaders already there, and of this callout's arrows before it
  const routesFor = (rect: Rect, thorough = false): LeaderRoute[] => {
    const routes: LeaderRoute[] = [];
    for (const a of aims) routes.push(routeAim(rect, a, space, { boxes: opts.avoid.filter((r) => !rectsOverlap(r, rect)), leaders: [...drawn, ...routes.map((r) => r.pts)] }, thorough));
    return routes;
  };
  const extras = [0, 4 * u, 10 * u, 18 * u, 30 * u, 48 * u, 72 * u];
  const colStep = Math.max(0.3 * w, 8 * u);
  const cands: Candidate[] = [];
  const area = w * h || 1;
  const baseCost = (rect: Rect, k: number, extra: number, below: boolean): number => {
    let cost = (extra / u) * 0.35 + Math.abs(k) * 1.5 + (below ? 4 : 0);
    for (const r of opts.avoid) {
      const ow = Math.min(rect.x + rect.w, r.x + r.w) - Math.max(rect.x, r.x);
      const oh = Math.min(rect.y + rect.h, r.y + r.h) - Math.max(rect.y, r.y);
      if (ow > 0 && oh > 0) cost += 40 + ((ow * oh) / area) * 100;
    }
    if (opts.prev) cost += Math.min(6, dist(opts.prev, rect) / (8 * u));
    // on top of another callout's leader: that leader would have to find a way round
    for (const l of drawn) for (let i = 0; i + 1 < l.length; i++) if (segmentIntersectsRect(l[i]!, l[i + 1]!, rect)) {
      cost += 30;
      break;
    }
    return cost;
  };
  for (let k = -7; k <= 7; k++) {
    const cx = anchor.x + k * colStep;
    const x = cx - w / 2;
    if (x < bounds.x || x + w > bounds.x + bounds.w) continue;
    let minY = Infinity,
      maxY = -Infinity;
    if (marks.length) {
      const x0 = x - 4 * u,
        x1 = x + w + 4 * u;
      for (const mk of marks)
        for (const p of mk.samples) {
          if (p.x < x0 || p.x > x1) continue;
          if (p.y < minY) minY = p.y;
          if (p.y > maxY) maxY = p.y;
        }
      if (minY === Infinity) {
        // off its one mark: not a place for it. Between several marks: above or below them all
        if (marks.length === 1) continue;
        for (const mk of marks) {
          minY = Math.min(minY, mk.bounds.y);
          maxY = Math.max(maxY, mk.bounds.y + mk.bounds.h);
        }
      }
    } else {
      minY = maxY = anchor.y;
    }
    for (const extra of extras) {
      const above = { x, y: minY - minGap - extra - h, w, h };
      const below = { x, y: maxY + minGap + extra, w, h };
      cands.push({ rect: above, cost: baseCost(above, k, extra, false) }, { rect: below, cost: baseCost(below, k, extra, true) });
    }
  }
  cands.sort((a, b) => a.cost - b.cost);
  type Placed = { rect: Rect; routes: LeaderRoute[]; cost: number; crossings: number };
  let best: Placed | null = null;
  // a spot where a leader crosses another mark is still far better than a box on one (a tight stack of bands)
  let crossing: Placed | null = null;
  let tried = 0;
  const tries = Math.min(40, 14 * aims.length);
  // one arrow likes to be upright; several fan out whatever the box does, so leaning costs them less
  const lean = aims.length > 1 ? 0.05 : 0.15;
  for (const c of cands) {
    if (best && c.cost > best.cost + 2) break;
    if (tried >= tries) break;
    if (!boxAllowed(c.rect, own, space)) continue;
    tried++;
    const routes = routesFor(c.rect);
    let cost = c.cost;
    for (const r of routes) {
      cost += r.face ? 0.04 * (routeLength(r) / u) : lean * (Math.abs(r.tip.x - r.pts[0]!.x) / u) + 0.02 * (dist(r.pts[0]!, r.tip) / u);
      // round an end is second best; a leader that has to find its way round something, a poor third;
      // one that would turn back on itself or come back past the box, a place to take only if nothing else will do
      cost += (r.face ? 3 : 0) + (r.detour ? 6 : 0) + (looksWrong(r.pts, r.side, u) || hugsBox(r.pts, c.rect, r.side, space.stub) ? 30 : 0);
    }
    const crossings = routes.filter((r) => !r.clear).length;
    // and one that runs alongside a mark, the poorer for every bit of it (weighed only for a place still in the running)
    if (!crossings && (!best || cost < best.cost)) for (const r of routes) cost += (0.15 * alongMarks(r.pts, space.marks, 10 * u, space.arrowLen + space.stub + 8 * u, 6 * u, u)) / u;
    if (crossings) {
      if (!crossing || crossings < crossing.crossings || (crossings === crossing.crossings && cost < crossing.cost)) crossing = { rect: c.rect, routes, cost, crossings };
      continue;
    }
    if (!best || cost < best.cost) best = { rect: c.rect, routes, cost, crossings };
  }
  const found = best ?? crossing;
  if (found) return { rect: found.rect, routes: found.routes };
  // Nothing clean anywhere near: sit above what it points at, clamped to the image.
  const top = marks.length ? Math.min(...marks.map((mk) => mk.bounds.y)) : anchor.y;
  const rect: Rect = {
    x: Math.max(bounds.x + u, Math.min(bounds.x + bounds.w - w - u, anchor.x - w / 2)),
    y: Math.max(bounds.y + u, Math.min(bounds.y + bounds.h - h - u, top - minGap - h)),
    w,
    h,
  };
  return { rect, routes: routesFor(rect) };
}
